import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import {
  SensitiveTableCell,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { formatDate } from '@utils/date.utils'
import { getCurrencySymbol } from '@utils/transaction.utils'

import { LoyaltyRewardsHistoryDto } from '@model/IntroducingBrokerLoyalty'

import styles from './ReportsTable.module.scss'

interface Props {
  tableQuery: PaginatedQueryResult<LoyaltyRewardsHistoryDto>
}

export const ReportsTable: FC<Props> = (props) => {
  const { tableQuery } = props
  const { data, isLoading } = tableQuery

  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { t } = useTranslation()

  const tableHeaderRows = [
    { name: t('Date'), id: '1' },
    { name: t('Event'), id: '2' },
    { name: t('Amount'), id: '3' },
  ]

  const eventMap = {
    created: {
      name: t('IB.Dashboard.Pending'),
      info: t('IB.Checking qualification criteria'),
    },
    approved: {
      name: t('IB.Became available'),
      info: '',
    },
    rejected: {
      name: t('Rejected'),
      info: t('IB.Qualification criteria was not met'),
    },
    redeemed: {
      name: t('Claimed'),
      info: '',
    },
  }

  return (
    <Table className={styles.table} isLoading={isLoading} noResults={!data.length}>
      <TableHeader>
        <TableRow>
          {tableHeaderRows?.map((option, index) => {
            return <TableHead key={`${option.name}_${index}`}>{option.name}</TableHead>
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((item, index) => {
          const event = eventMap[item.rewardStatus?.name.toLowerCase() as keyof typeof eventMap]
          const eventInfo = event?.name ? `${event.name} - ${item.tier.name}` : ''

          return (
            <TableRow key={`${item.tier.id}_${index}`}>
              <SensitiveTableCell className={styles.tableCell}>
                {formatDate(item.timestamp, {
                  formatType: dateFormat,
                  withTime: false,
                })}
              </SensitiveTableCell>

              <SensitiveTableCell className={styles.tableCell}>
                {' '}
                <Tooltip content={event?.info} disabled={!event?.info} direction='top'>
                  {eventInfo}
                </Tooltip>
              </SensitiveTableCell>
              <SensitiveTableCell className={styles.tableCell}>
                {`${getCurrencySymbol('USD')}${item.amount}`}
              </SensitiveTableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
